import { useState, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { getNativeBridge } from "@/lib/nativeBridge";

interface PushSubscriptionState {
  isSupported: boolean;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
}

export function usePushNotifications() {
  const [state, setState] = useState<PushSubscriptionState>({
    isSupported: false,
    isSubscribed: false,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const checkSupport = async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setState(prev => ({ ...prev, isSupported: false, isLoading: false }));
        return;
      }

      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        setState({
          isSupported: true,
          isSubscribed: !!subscription,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        setState(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: "Error al verificar estado de notificaciones" 
        }));
      }
    };

    checkSupport();
  }, []);

  const subscribeMutation = useMutation({
    mutationFn: async () => {
      const registration = await navigator.serviceWorker.ready;
      
      const vapidResponse = await fetch('/api/notifications/vapid-key', {
        credentials: 'include'
      });
      
      if (!vapidResponse.ok) {
        throw new Error("Error al obtener clave VAPID");
      }
      
      const { publicKey } = await vapidResponse.json();
      
      let subscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });
      } catch (pushError: any) {
        if (pushError.name === 'NotAllowedError') {
          throw new Error("Permiso de notificaciones denegado");
        } else if (pushError.message?.includes('push service')) {
          throw new Error("Las notificaciones push no están disponibles en este navegador o entorno");
        }
        throw new Error("Error al registrar notificaciones: " + (pushError.message || "Error desconocido"));
      }

      const subJson = subscription.toJSON();
      const response = await apiRequest('POST', '/api/push/subscribe', {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      if (!response.ok) {
        throw new Error("Error al guardar subscripción");
      }

      return subscription;
    },
    onSuccess: () => {
      setState(prev => ({ ...prev, isSubscribed: true, error: null }));
      // Mirror the toggle into the native shell (no-op in plain browser).
      try {
        getNativeBridge()?.notifyPushEnabled?.();
      } catch {}
    },
    onError: (error: Error) => {
      setState(prev => ({ ...prev, error: error.message }));
    },
  });

  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        
        await apiRequest('POST', '/api/push/unsubscribe', {
          endpoint: subscription.endpoint,
        });
      }
    },
    onSuccess: () => {
      setState(prev => ({ ...prev, isSubscribed: false, error: null }));
      try {
        getNativeBridge()?.notifyPushDisabled?.();
      } catch {}
    },
    onError: (error: Error) => {
      setState(prev => ({ ...prev, error: error.message }));
    },
  });

  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      setState(prev => ({ ...prev, error: "Este navegador no soporta notificaciones" }));
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === "granted";
  }, []);

  const subscribe = useCallback(async () => {
    const hasPermission = await requestPermission();
    if (!hasPermission) {
      setState(prev => ({ ...prev, error: "Permiso de notificaciones denegado" }));
      return;
    }
    
    subscribeMutation.mutate();
  }, [requestPermission, subscribeMutation]);

  const unsubscribe = useCallback(() => {
    unsubscribeMutation.mutate();
  }, [unsubscribeMutation]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    isSubscribing: subscribeMutation.isPending,
    isUnsubscribing: unsubscribeMutation.isPending,
  };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
